import React, { useRef, useState, useEffect, useContext } from 'react';
import { motion } from "framer-motion";
import { Row, Col, Container, Modal } from "react-bootstrap";
import Cookies from 'js-cookie';
import Loader from "../NavBar/Loader/Loader.jsx";
import CollapsibleSection from '../CollapsibleSection/CollapsibleSection';
import { loadingContext, loginContext } from '../../Context';
import StudyProgressConfig from '../../helpers/StudyProgressBarConfig.js';
import EmotionModelHandler from '../../helpers/EmotionModelHandler'; 
import MongoRequestHandler from '../../helpers/MongoRequestHandler';
import { EmotionPredictionTable, EmotionPredictionList } from '../PredictionResultTable/PredictionResultTable';
import ImageGalleryModal from '../Modals/ImageGalleryModal.jsx';
import StudyConfirmationModal from '../Modals/CompleteStudyModal.jsx';
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import './StudyOne.css';


const startJoyRide = () => {
    const driverObj = driver({
        showProgress: true,
        popoverClass: 'driverjs-theme',
        steps: [
            { element: '.start-camera-button',  popover: { title: 'Start Camera', description: 'Click on the Start Camera button to start the camera stream on your device!', side: "top", align: 'start' }},
            { element: '.capture-photo-button', popover: { title: 'Capture Photo', description: 'Click on the Capture Photo button to capture a photo for recognition!', side: "top", align: 'start' }},
            { element: '.process-image-button', popover: { title: 'Process Image', description: 'Then, click on the Process Image button to send your captured photo to the backend for recognition!', side: "top", align: 'start' }},
            { element: '.upload-image-button', popover: { title: 'Upload Image', description: 'Friendly reminder, you can also upload images from your local machine for recognition!', side: "top", align: 'start' }},
            { element: '.select-from-gallery-button', popover: { title: 'Select From Our Samples', description: 'Or, you can select sample images from your our database!', side: "top", align: 'start' }},
            { element: '.restart-tour-button', popover: { title: 'Restart Tour', description: 'Friendly reminder, you can also click on the Restart Tour button to restart the guided tour!', side: "top", align: 'start' }},
            { popover: { title: 'Enjoy EmoAI!', description: 'That\'s it! Go ahead and start enjoying playing with EmoAI!' }}
        ]
    });

    driverObj.drive();
    
    // prevent the tour from showing again after the user has completed it
    Cookies.set('is_tour_complete', 'true', { expires: 7 });
}



const StudyOne = () => {
    const PROCESSING_HISTORY_SOURCE = "study_one";
    const IMAGE_SIZE_LIMIT = 3 * 1024 * 1024; // 3 MB in bytes
    const fileInputRef = useRef(null);
    const imageInputRef = useRef(null);
    const imageCanvasRef = useRef(null);
    const annotationHelperRef = useRef(null);
    const cameraStreamRef = useRef(null);

    const videoRef = useRef(null);
    const [isCameraOn, setIsCameraOn] = useState(false);

    const originalImageRef = useRef(null);
    const gradCamImageRef = useRef(null);
    const guidedGradCAMImageRef = useRef(null);
    const [emotionResults, setEmotionResults] = useState([]);

    const loadingCtx = useContext(loadingContext);
    const isLoading = loadingCtx.state.isLoading;
    const [isErrorLoadingHistory, setIsErrorLoadingHistory] = useState(false);

    const loginCtx = useContext(loginContext);
    const isLoggedIn = loginCtx.state.isLoggedIn;
    const [processingHistory, setProcessingHistory] = useState([]);
    
    const [showGalleryModal, setShowGalleryModal] = useState(false);
    const [showCompleteStudyModal, setShowCompleteStudyModal] = useState(false);

    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorModalMessage, setErrorModalMessage] = useState('');
    const [errorModalTitle, setErrorModalTitle] = useState('');

    const [refreshHistory, setRefreshHistory] = useState(true);

    const [ canvasSize, setCanvasSize ] = useState({ width: 224, height: 224 });

    const progressConfig = new StudyProgressConfig();
    const [showTooltip1, setShowTooltip1] = useState(false);
    const [showTooltip2, setShowTooltip2] = useState(false);
    const [isAzureOnline, setIsAzureOnline] = useState(false);
  
    const handleMouseEnter1 = () => {
      setShowTooltip1(true);
    };
  
    const handleMouseLeave1 = () => {
      setShowTooltip1(false);
    };
  
    const handleMouseEnter2 = () => {
      setShowTooltip2(true);
    };
  
    const handleMouseLeave2 = () => {
      setShowTooltip2(false);
    };
  



    const handleShowErrorModal = (isShow, title, message) => {
        setShowErrorModal(isShow);

        if(title)
            setErrorModalTitle(title);

        if(message)
            setErrorModalMessage(message);

    };

    const uploadImageOnClick = () => {
        fileInputRef.current.click();
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (file.size > IMAGE_SIZE_LIMIT) {
            handleShowErrorModal(true, 'Error', 'File size exceeds the maximum 3 MB limit. Please select a smaller file.');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const image = new Image();
            image.onload = () => {
                if (imageCanvasRef.current) {
                    const canvas = imageCanvasRef.current;
                    const ctx = canvas.getContext('2d');
                    
                    canvas.width = image.width;
                    canvas.height = image.height;
                    ctx.drawImage(image, 0, 0);

                    setCanvasSize({ width: canvas.width, height: canvas.height });
                }
                if (imageInputRef.current) {
                    imageInputRef.current.src = image.src;
                }
            };
            image.src = e.target.result;
        };
        reader.readAsDataURL(file);
    };
    
    const fetchStatusAndInitialize = async () => {
        try {
            const status = await new EmotionModelHandler().testServerStatus();
            setIsAzureOnline(status);
        } catch (error) {
            console.error("Failed to check Azure status:", error);
            setIsAzureOnline(false);
        }
    };

    useEffect(() => {
        fetchStatusAndInitialize();

        console.log("is_tour_complete", Cookies.get('is_tour_complete'));
        if(Cookies.get('is_tour_complete') != 'true') {
            startJoyRide()
        }
    }, []);

    useEffect(() => {
        if (isCameraOn) {
            startCamera();
        } else {
            stopCamera();
        }
    }, [isCameraOn]);

    const startCamera = () => {
        navigator.mediaDevices.getUserMedia({ video: true })
            .then((stream) => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play();

                    // Store the stream so we can access it later for proper turn off
                    cameraStreamRef.current = stream;
                }
            })
            .catch((err) => {
                console.error("Error accessing camera: ", err);
            });
    };
    
    const stopCamera = () => {
        // Stop all media tracks from the cameraStreamRef
        if (cameraStreamRef.current) {
            cameraStreamRef.current.getTracks().forEach((track) => {
                if (track.readyState === 'live' && track.kind === 'video') {
                    track.stop();  // Stops the camera hardware
                }
            });
        }
    
        // Clear the video element's source
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    
        // Clear the reference to the stream
        cameraStreamRef.current = null;
    };

    const capturePhoto = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (videoRef.current) {
            canvas.width = videoRef.current.videoWidth;
            canvas.height = videoRef.current.videoHeight;

            // horizontally flip the photo taken from the camera feed
            ctx.translate(canvas.width, 0);
            ctx.scale(-1, 1);

            ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
            const imageData = canvas.toDataURL('image/png');
            if (imageInputRef.current) {
                imageInputRef.current.src = imageData;
            }

            if (imageCanvasRef.current) {
                const image = new Image();
                image.onload = () => {
                    image.src = imageData;
                    
                    imageCanvasRef.current.width = image.width;
                    imageCanvasRef.current.height = image.height;
                    ctx.drawImage(image, 0, 0);

                    if (annotationHelperRef.current) {
                        annotationHelperRef.current.originalImageData = ctx.getImageData(0, 0, imageCanvasRef.current.width, imageCanvasRef.current.height);
                    }
                };
            }

            // clear file input on capturing photo
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

            // stopCamera();
            setIsCameraOn(prev => !prev);
        }
    };
    
    useEffect(() => {
        if (isLoggedIn && refreshHistory) {
            setIsErrorLoadingHistory(false);
            loadingCtx.dispatch({ type: "on" });

            try{
                new MongoRequestHandler().fetchProcessHistory(loginCtx.state.username, PROCESSING_HISTORY_SOURCE)
                    .then((data) => {
                        setProcessingHistory(data);
                        setRefreshHistory(false);
                        loadingCtx.dispatch({ type: "off" });
                    });
            } catch (error) {
                console.error('Error loading processing history:', error);
                setIsErrorLoadingHistory(true);
                loadingCtx.dispatch({ type: "off" });
            }
        }

    }, [isLoggedIn, refreshHistory]);


    const processImageButton = async () => {
        if (!imageInputRef.current.src && !fileInputRef.current.files.length) {
            handleShowErrorModal(true, 'Error', 'No image has been uploaded yet.');
            return;
        }
    
        const emoHelper = new EmotionModelHandler(isAzureOnline);
        const mongoHelper = new MongoRequestHandler();
        const loggedInUser = loginCtx.state.username;
    
        const maskCoordinates = [];
        const drawCoordinates = [];
    
        loadingCtx.dispatch({ type: "on" });
    
        const formData = new FormData();
        if (fileInputRef.current.files.length) {
            formData.append('image', fileInputRef.current.files[0]);
        } else {
            const blob = await fetch(imageInputRef.current.src).then(r => r.blob());
            formData.append('image', blob, 'captured.png');
        }

        // no need to pass in the following, passing in as hardcoded
        formData.append('mask_coordinates', JSON.stringify(maskCoordinates));
        formData.append('draw_coordinates', JSON.stringify(drawCoordinates));
        formData.append('canvas_size', JSON.stringify(canvasSize));
        formData.append('noise_type', 'gaussian');
        formData.append('noise_intensity', 0);
    
        console.log('file', fileInputRef.current.files[0]);
        console.log('mask_coordinates', JSON.stringify(maskCoordinates));
        console.log('canvas_size', JSON.stringify(canvasSize));
    
        try {
            const responseData = await emoHelper.processImage(formData);
            const { original, masked_image, gradcam_image, guided_gradcam_image, results } = responseData;
            const emoPredResult = emoHelper.extractAndSortEmotionOutputs(results);
            console.log("responseData", responseData);

            const historyData = {
                username: loggedInUser,
                input_image: `data:image/png;base64,${original}`,
                annotated_image: `data:image/png;base64,${masked_image}`,
                gradcam: `data:image/png;base64,${gradcam_image}`,
                guided_gradcam: `data:image/png;base64,${guided_gradcam_image}`,
                emo_pred: emoPredResult,
                history_source: PROCESSING_HISTORY_SOURCE
            };

            originalImageRef.current.src = historyData.input_image;
            gradCamImageRef.current.src = historyData.gradcam;
            guidedGradCAMImageRef.current.src = historyData.guided_gradcam;

            setEmotionResults(emoPredResult);
            mongoHelper.handleInsertHistory(historyData).then(setRefreshHistory(true));
                
        } catch (error) {
            console.error('Error processing the image:', error);
        }
    
        loadingCtx.dispatch({ type: "off" });
    };

    const handleGalleryImageSelect = (image) => {
        if (imageInputRef.current) {
            imageInputRef.current.src = image.src;
        }
        setShowGalleryModal(false);
    };

    return (
        <div className="content">
            <div className="tabcontent">
                <h3 className='text' style={{ textAlign: "center", paddingTop: "1.25rem" }}>Let's explore Emotion AI!</h3>

                <Container className='custom-container'>
                    <Row>
                        <Col>
                            <CollapsibleSection collapsedContent="CONTROLS" openByDefault={false} cookiePreventState={"user_collapsed_instruction"} fixed={true} enableHover={false}>
                                <div>
                                    <motion.button
                                        type="button"
                                        className="col button restart-tour-button me-3"
                                        title="Restart Tour"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.8 }}
                                        onClick={ startJoyRide }
                                    >
                                        <span className="text">Restart Tour</span>
                                    </motion.button>
                                    <motion.button
                                        type="button"
                                        className="col button button-danger me-2"
                                        title="Complete Study One"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.8 }}
                                        onClick={() => setShowCompleteStudyModal(true)}
                                    >
                                        Complete Study One
                                    </motion.button>
                                </div>
                            </CollapsibleSection>
                        </Col>
                    </Row>                        
                </Container>

                <Container className='custom-container'>
                    <Row className="message-card" style={{ minHeight: '56vh' }}>
                        <Col xs={12} sm={6}>
                            <div id="imageLayerContainer">
                                <img id="imageInput" ref={imageInputRef}/>
                                {/* <canvas id="imageCanvas" ref={imageCanvasRef}/> */}
                                {isCameraOn && <video ref={videoRef} id="videoOverlay" />}
                            </div>

                            <Container>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: "none" }}
                                    accept="image/*"
                                    onChange={handleImageChange}
                                />

                                <Row style={{ gap: "0.25rem" }}>
                                    <motion.button
                                        type="button"
                                        className="col button start-camera-button"
                                        title="Start Camera"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.8 }}
                                        onClick={() => {
                                            setIsCameraOn(prev => !prev);
                                        }}
                                    >
                                        {isCameraOn ? <i className='bx bx-video-off control-button-icon' /> : <i className='bx bx-video control-button-icon' />}
                                        <span className="text control-button-text">Video</span>
                                    </motion.button>
                                    {isCameraOn && 
                                        <motion.button
                                            type="button"
                                            className="col button capture-photo-button"
                                            title="Upload Image"
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.8 }}
                                            id="uploadImageButton"
                                            onClick={capturePhoto}
                                        >
                                            <i className='bx bx-camera control-button-icon' />
                                            <span className="text control-button-text">Capture</span>
                                        </motion.button>
                                    }
                                    <motion.button
                                        type="button"
                                        className="col button upload-image-button"
                                        title="Upload Image"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.8 }}
                                        id="uploadImageButton"
                                        onClick={uploadImageOnClick}
                                    >
                                        <i className='bx bx-upload control-button-icon' />
                                        <span className="text control-button-text">Upload</span>
                                    </motion.button>

                                    <motion.button
                                        type="button"
                                        className="col button select-from-gallery-button"
                                        title="Restart Tour"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.8 }}
                                        onClick={ () => setShowGalleryModal(true) }
                                    >
                                        <i className='bx bx-photo-album control-button-icon' />
                                        <span className="text control-button-text">Gallery</span>
                                    </motion.button>
                                    <motion.button
                                        type="button"
                                        className="col button process-image-button ms-4"
                                        title="Process Image"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.8 }}
                                        onClick={ processImageButton }
                                        disabled={isLoading ? true : false}
                                    >
                                        <i className='bx bx-paper-plane control-button-icon' />
                                        <span className="text control-button-text">Process</span>
                                    </motion.button>
                                </Row>
                            </Container>

                            <div id="error-message" className="error-message"></div>
                        </Col>

                        <Col xs={12} sm={6}>
                            <h4 className="text">{isLoading ? "Processing..." : "Results"}</h4>
                            {
                                isLoading ? 
                                <Loader /> 
                                :
                                <>
                                    <EmotionPredictionTable predictions={emotionResults} maxHeight={'100%'}/>
                                    {emotionResults.length > 0 && (
                                        <p className="highlight sentence">
                                            AI might make mistakes. It is okay if you disagree with it.
                                        </p>
                                    )}
                                </>
                            }
                        </Col>
                    </Row>

                    <Row className="message-card" >
                        <h4 className="text">Visualizations</h4>

                        <Col xs={12} sm={6} md={4}>
                            <h5 className="text">Original</h5>
                            <img id="originalImage" className="visualization-images" ref={originalImageRef} />
                        </Col>
                        <Col xs={12} sm={6} md={4}>
                            <h5>
                            <span className="text">Method One</span>
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                                <i
                                onMouseEnter={handleMouseEnter1}
                                onMouseLeave={handleMouseLeave1}
                                className="bx bx-info-circle info-icon"
                                />
                                {showTooltip1 && (
                                <div 
                                style={{
                                    position: 'absolute',
                                    transform: 'translateY(-130%)',
                                    width: '250px',
                                    backgroundColor: '#ffffff',
                                    color: '#000000', 
                                    padding: '10px',
                                    borderRadius: '4px',
                                    fontSize: 'small',
                                    fontWeight:'lighter',
                                    transition: 'opacity 0.2s',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                                  }}>
                                    Grad-Cam highlights the areas that the model generally focuses on, with clearer regions indicating higher attention 
                                    and blurrier, darker areas indicating lower attention.
                                </div>
                                )}
                            </div>
                            </h5>
                            <img id="gradCamImage" className="visualization-images" ref={gradCamImageRef} />
                        </Col> 

                        <Col xs={12} sm={6} md={4}>
                            <h5>
                            <span className="text">Method Two</span>
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                                <i
                                onMouseEnter={handleMouseEnter2}
                                onMouseLeave={handleMouseLeave2}
                                className="bx bx-info-circle info-icon"
                                />
                                {showTooltip2 && (
                                <div 
                                style={{
                                    position: 'absolute',
                                    transform: 'translateY(-130%)',
                                    width: '250px',
                                    backgroundColor: '#ffffff',
                                    color: '#000000', 
                                    padding: '10px',
                                    borderRadius: '4px',
                                    fontSize: 'small',
                                    fontWeight:'lighter',
                                    transition: 'opacity 0.2s',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                                  }}>
                                    Guided Grad-Cam shows higher color saturation outlining the specific regions of interest.
                                </div>
                                )}
                            </div>
                            </h5>
                            <img id="guidedGradCAMImage" className="visualization-images" ref={guidedGradCAMImageRef} />
                        </Col> 
                    </Row>
                    
                    <Row className="message-card">
                        <h4 className="text">Process History</h4>
                        {isLoggedIn ? (
                            !isErrorLoadingHistory && processingHistory ? (
                                <>
                                    <div className="grid-container-4">
                                        <div className="grid-item">
                                            <h5 className="text">Original</h5>
                                        </div>
                                        <div className="grid-item">
                                            <h5 className="text">Method One</h5>
                                        </div>
                                        <div className="grid-item">
                                            <h5 className="text">Method Two</h5>
                                        </div>
                                        <div className="grid-item">
                                            <h5 className="text">Predictions</h5>
                                        </div>
                                    </div>

                                    {processingHistory.map((entry, index) => (
                                        <div className="grid-container-4" key={index}>
                                            <div className="grid-item">
                                                <img src={entry.input_image} className="visualization-images" alt="Original" />
                                            </div>
                                            <div className="grid-item">
                                                <img src={entry.gradcam} className="visualization-images" alt="GradCAM" />
                                            </div>
                                            <div className="grid-item">
                                                <img src={entry.guided_gradcam} className="visualization-images" alt="Guided GradCAM" />
                                            </div>
                                            <div className="grid-item">
                                                <EmotionPredictionList predictions={entry.emo_pred} showFirstCount={7} fontSize={0.9} maxHeight={"80%"}/>
                                            </div>
                                        </div>
                                    ))}
                                </>
                            ) : (
                                <span>Error loading processing history, please check server</span>
                            )
                        ) : (
                            <span>Please log in to record and view your processing history</span>
                        )}
                    </Row>

                </Container>

                <Modal show={showErrorModal} onHide={() => handleShowErrorModal(false)}>
                    <Modal.Header closeButton>
                    <Modal.Title>{errorModalTitle}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>{errorModalMessage}</Modal.Body>
                    <Modal.Footer>
                        <motion.button 
                            type="button"
                            className="button carousel-button"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.8 }}
                            onClick={() => handleShowErrorModal(false)}
                        >
                            Got it!
                        </motion.button>
                    </Modal.Footer>
                </Modal>

                <ImageGalleryModal
                    show={showGalleryModal}
                    onClose={() => setShowGalleryModal(false)}
                    onImageSelect={handleGalleryImageSelect}
                    availableFor={"study_one"}
                />

                <StudyConfirmationModal
                    show={showCompleteStudyModal}
                    handleClose={() => setShowCompleteStudyModal(false)}
                    handleConfirm={() => setShowCompleteStudyModal(true)}
                    title={"Complete Study One"}
                    message={"Are you sure you want to complete Study One?"}
                    overWriteUrl={"/exploration_intro_session_one"}
                    progressOverwrite={progressConfig.getStudyOneCompletionProgress()}
                />

            </div>
        </div>
    );
};

export default StudyOne;